import { Box, Skeleton, Stack, type Theme, Tooltip, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DashboardCard from '/@/components/bayer/dashboard-card';
import type { UserNodeRecommendationsDashboardResponse } from '/@/hooks/dashboards';
import { useGrowers } from '/@/hooks/growers';
import { parseUserNode, useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getAppInstance, getCornRecommendationEnabled, getSoyRecommendationEnabled } from '/@/store/session/selectors';
import type { Grower } from '/@/types/growers';
import { objectEntries } from '/@/utils';
import { reverse } from '/@/utils/router';
import { getRecommendationTypes } from '../../../hooks/crops';

const MAX_BAR_HEIGHT = 100;

// Move recommendationTypesFiltered outside component to avoid recreation
const getRecommendationTypesFiltered = () => {
    const recommendationTypes = getRecommendationTypes();
    return {
        corn: {
            ...recommendationTypes.corn,
            types: ['corn', 'density'] as const,
            tooltip: (count: number) =>
                `${count} ${_('de {{type}}', {
                    type: _('Híbridos'),
                })}`,
        },
        soy: {
            ...recommendationTypes.soy,
            types: ['soy'] as const,
            tooltip: (count: number) =>
                `${count} ${_('de {{type}}', {
                    type: _('Soja'),
                })}`,
        },
        herbicide: {
            ...recommendationTypes.herbicide,
            types: ['herbicide'] as const,
            tooltip: (count: number) =>
                `${count} ${_('de {{type}}', {
                    type: _('Herbicidas'),
                })}`,
        },
        fungicide: {
            ...recommendationTypes.fungicide,
            types: ['fungicide'] as const,
            tooltip: (count: number) =>
                `${count} ${_('de {{type}}', {
                    type: _('Fungicidas'),
                })}`,
        },
        insecticide: {
            ...recommendationTypes.insecticide,
            types: ['insecticide'] as const,
            tooltip: (count: number) =>
                `${count} ${_('de {{type}}', {
                    type: _('Insecticidas'),
                })}`,
        },
    };
};

const RECOMMENDATION_TYPES_FILTERED = getRecommendationTypesFiltered();

const getCountByTypes = (
    countByType: UserNodeRecommendationsDashboardResponse['countByType'],
    types: readonly string[]
): number => {
    return types.reduce((acc, type) => {
        // Map 'fungicide' to 'funguicide' to match API response
        const apiType = type === 'fungicide' ? 'funguicide' : type;
        return acc + ((countByType as unknown as Record<string, number | undefined>)?.[apiType] || 0);
    }, 0);
};

const getBarHeight = (count: number, maxCategoryRecommendations: number): number => {
    if (maxCategoryRecommendations === 0) return 0;
    return (count / maxCategoryRecommendations) * MAX_BAR_HEIGHT;
};

const GrowerRecommendationsCardSkeleton = memo(() => (
    <Stack
        spacing={1.5}
        alignItems='center'
        justifyContent='center'
        textAlign='center'
        height='100%'
        alignSelf='end'
        mb='auto'
    >
        <Stack direction='row' spacing={0.5}>
            {Array.from({ length: 5 }).map((_, index) => (
                <Stack key={`skeleton-bar-${index}`} spacing={0.5} alignItems='center' justifyContent='center'>
                    <Box
                        sx={{
                            height: '100%',
                        }}
                    >
                        <Skeleton variant='rectangular' width={12} height={60} sx={{ borderRadius: '20px' }} />
                    </Box>
                    <Skeleton variant='circular' width={16} height={16} />
                </Stack>
            ))}
        </Stack>
        <Skeleton variant='text' width={40} height={32} />
        <Stack spacing={0.5} height='100%'>
            <Skeleton variant='text' width={120} height={24} />
            <Skeleton variant='text' width={100} height={20} />
        </Stack>
    </Stack>
));

const GrowerRecommendationsCard = memo(
    ({
        grower,
        parsedUserNode,
        maxCategoryRecommendations,
    }: {
        grower: Grower;
        parsedUserNode: ReturnType<typeof parseUserNode>;
        maxCategoryRecommendations: number;
    }) => {
        const navigate = useNavigate();
        // FIXME: `parent` is deprecated, use `parents?.[0]` instead
        const parentUserNodeQuery = useGetUserNode(parsedUserNode.parent ?? 0);

        const countByType = grower.recommendationStatistics?.countByType;
        const totalCount = grower.recommendationStatistics?.count;

        const handleNavigate = useCallback(() => {
            navigate(
                reverse('Growers.detail', {
                    params: {
                        growerNodeId: grower.id,
                    },
                })
            );
        }, [navigate, grower.id]);

        const appInstance = useSelector(getAppInstance);
        const soyRecommendationIsEnabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
        const cornRecommendationIsEnabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));

        const barData = useMemo(() => {
            if (!countByType) return null;
            return objectEntries(RECOMMENDATION_TYPES_FILTERED)
                .filter(([key, value]) => {
                    if (key === 'corn' && !cornRecommendationIsEnabled) return false;
                    if (key === 'soy' && !soyRecommendationIsEnabled) return false;
                    return true;
                })
                .map(([key, value]) => {
                    const count = getCountByTypes(countByType, value.types);
                    const barHeight = getBarHeight(count, maxCategoryRecommendations);
                    return { key, value, count, barHeight };
                });
        }, [countByType, maxCategoryRecommendations, cornRecommendationIsEnabled, soyRecommendationIsEnabled]);

        const isLoading = !grower.recommendationStatistics;

        if (isLoading) {
            return <GrowerRecommendationsCardSkeleton />;
        }

        return (
            <Stack
                spacing={1.5}
                alignItems='center'
                justifyContent='center'
                textAlign='center'
                height='100%'
                alignSelf='end'
                mb='auto'
            >
                <Stack direction='row' spacing={0.5}>
                    {barData?.map(({ key, value, count, barHeight }) => (
                        <Tooltip key={key} title={value.tooltip(count)}>
                            <Stack spacing={0.5} alignItems='center' justifyContent='center'>
                                <Box
                                    sx={{
                                        height: `${MAX_BAR_HEIGHT}px`,
                                        position: 'relative',
                                    }}
                                >
                                    <Box height={`${MAX_BAR_HEIGHT - barHeight}px`} />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            width: '12px',
                                            height: `${barHeight}px`,
                                            borderRadius: '20px',
                                            background: '#CDF1FF',
                                        }}
                                    />
                                </Box>
                                <Box
                                    mt='auto'
                                    alignItems='center'
                                    justifyContent='center'
                                    display='flex'
                                    sx={{
                                        width: '16px !important',
                                        height: '16px !important',

                                        '& svg *': {
                                            fill: (t: Theme) => `${t.palette.primary.main} !important`,
                                        },
                                    }}
                                >
                                    {value.icon}
                                </Box>
                            </Stack>
                        </Tooltip>
                    ))}
                </Stack>
                <Typography variant='h6'>{totalCount}</Typography>
                <Stack spacing={0.5} height='100%'>
                    <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        color='primary'
                        sx={{
                            cursor: 'pointer',
                            '&:hover': {
                                opacity: 0.8,
                            },
                            wordBreak: 'break-word',
                            mt: 'auto !important',
                        }}
                        onClick={handleNavigate}
                    >
                        {parsedUserNode.name}
                    </Typography>
                    <Typography
                        variant='caption'
                        color='primary.mui.inactive'
                        sx={{
                            wordBreak: 'break-word',
                            mt: 'auto !important',
                        }}
                    >
                        {parentUserNodeQuery.data?.name}
                    </Typography>
                </Stack>
            </Stack>
        );
    }
);

GrowerRecommendationsCard.displayName = 'GrowerRecommendationsCard';

const GrowersWithMostRecommendationsDashboard = ({ userNodeId, year }: { userNodeId: number; year: number }) => {
    const growersQuery = useGrowers({ page: 1, pageSize: 10000, node: userNodeId, nodeType: 'grower', year });

    const sortedGrowers = useMemo(() => {
        if (!growersQuery.data?.results) return [];
        return [...growersQuery.data.results].sort((a, b) => {
            const countA = a.recommendationStatistics?.count ?? 0;
            const countB = b.recommendationStatistics?.count ?? 0;
            return countB - countA; // Sort descending (highest count first)
        });
    }, [growersQuery.data?.results]);

    const maxCategoryRecommendations = useMemo(() => {
        if (!growersQuery.data?.results) return 0;
        let maxValue = 0;
        growersQuery.data.results.forEach((grower) => {
            const countByType = grower.recommendationStatistics?.countByType;
            if (countByType) {
                objectEntries(RECOMMENDATION_TYPES_FILTERED).forEach(([, value]) => {
                    const count = getCountByTypes(countByType, value.types);
                    if (count > maxValue) {
                        maxValue = count;
                    }
                });
            }
        });
        return maxValue;
    }, [growersQuery.data?.results]);

    const parsedGrowers = useMemo(() => {
        if (!sortedGrowers.length) return [];
        return sortedGrowers.map((grower) => ({
            grower,
            parsedUserNode: parseUserNode(grower),
        }));
    }, [sortedGrowers]);

    const isLoading = !growersQuery.data && growersQuery.isValidating;

    return (
        <DashboardCard title={_('Productores con más recomendaciones')}>
            <Stack direction='row' overflow='auto' justifyContent='space-between'>
                {isLoading ? (
                    (['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5', 'sk-6'] as const).map((id) => (
                        <Box
                            key={id}
                            sx={{
                                width: 120,
                                flexShrink: 0,
                            }}
                        >
                            <GrowerRecommendationsCardSkeleton />
                        </Box>
                    ))
                ) : maxCategoryRecommendations > 0 ? (
                    parsedGrowers.slice(0, 10).map(({ grower, parsedUserNode }, index) => (
                        <Box
                            key={grower.id}
                            sx={{
                                display: index < 10 ? 'block' : 'none',
                                width: 120,
                                flexShrink: 0,
                            }}
                        >
                            <GrowerRecommendationsCard
                                grower={grower}
                                parsedUserNode={parsedUserNode}
                                maxCategoryRecommendations={maxCategoryRecommendations}
                            />
                        </Box>
                    ))
                ) : (
                    <Stack direction='column' alignItems='center' justifyContent='center' width='100%' spacing={'0.5'}>
                        <Typography variant='body1' color='primary' fontWeight='bold'>
                            {_('Todavía no hay productores con recomendaciones en esta campaña.')}
                        </Typography>
                        <Typography variant='body1' color='primary'>
                            {_('Esta sección se completa a medida que se generen recomendaciones.')}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </DashboardCard>
    );
};

export default GrowersWithMostRecommendationsDashboard;
